import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, DatePicker, Input, Select, Space, Popconfirm, Tag, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TeamMember, ScheduleOverride, TimeBlock } from "../../../types/admin/TeamTypes";
import { fetchScheduleOverrides, createScheduleOverride, updateScheduleOverride, deleteScheduleOverride } from "../../../api/admin/scheduleOverrideApi";
import { useNotifications } from "../../../context/NotificationContext";
import { SectionCard } from "../../../components/shared";
import ScheduleBlockEditor from "./ScheduleBlockEditor";
import { toWallClock } from "../../../utils/schedule/wallClock";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface UserProfileScheduleOverridesProps {
	teamMember: TeamMember;
	isEditing: boolean;
	onOverridesChange: () => void;
}

const UserProfileScheduleOverrides: React.FC<UserProfileScheduleOverridesProps> = ({ teamMember, isEditing, onOverridesChange }) => {
	const { showSuccess, showError } = useNotifications();
	const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [editingOverride, setEditingOverride] = useState<ScheduleOverride | null>(null);
	const [form] = Form.useForm();
	const [scheduleBlocks, setScheduleBlocks] = useState<TimeBlock[]>([]);

	useEffect(() => {
		loadOverrides();
	}, [teamMember.id]);

	const loadOverrides = async () => {
		setLoading(true);
		try {
			const response = await fetchScheduleOverrides(teamMember.id);
			setOverrides(response.data);
		} catch (error) {
			showError("Failed to load schedule overrides");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = () => {
		setEditingOverride(null);
		setScheduleBlocks([]);
		form.resetFields();
		form.setFieldsValue({
			override_type: "block",
		});
		setModalVisible(true);
	};

	const handleEdit = (override: ScheduleOverride) => {
		setEditingOverride(override);
		// Normalise on load: a row written before the wall-clock cutover would otherwise be resent
		// verbatim and 422 on a save that only edited the reason (86d42y068 S7).
		setScheduleBlocks((override.schedule_data || []).map((block) => ({ ...block, start: toWallClock(block.start), end: toWallClock(block.end) })));
		form.setFieldsValue({
			date_range: [dayjs(override.start_date), dayjs(override.end_date)],
			override_type: override.override_type,
			reason: override.reason,
		});
		setModalVisible(true);
	};

	const handleDelete = async (overrideId: number) => {
		try {
			await deleteScheduleOverride(teamMember.id, overrideId);
			showSuccess("Schedule override deleted");
			loadOverrides();
			onOverridesChange();
		} catch (error) {
			showError("Failed to delete schedule override");
			console.error(error);
		}
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			const [startDate, endDate] = values.date_range;

			const overrideData: Partial<ScheduleOverride> = {
				start_date: startDate.format("YYYY-MM-DD"),
				end_date: endDate.format("YYYY-MM-DD"),
				override_type: values.override_type,
				reason: values.reason,
				schedule_data: values.override_type === "block" ? null : scheduleBlocks.length > 0 ? scheduleBlocks : null,
			};

			if (editingOverride) {
				await updateScheduleOverride(teamMember.id, editingOverride.id, overrideData);
				showSuccess("Schedule override updated");
			} else {
				await createScheduleOverride(teamMember.id, overrideData);
				showSuccess("Schedule override created");
			}

			setModalVisible(false);
			form.resetFields();
			loadOverrides();
			onOverridesChange();
		} catch (error: any) {
			if (error.response?.status === 409) {
				showError("This date range overlaps with an existing override");
			} else {
				showError("Failed to save schedule override");
			}
			console.error(error);
		}
	};

	const columns = [
		{
			title: "Type",
			dataIndex: "override_type",
			key: "override_type",
			render: (type: string) => {
				const colors = {
					block: "var(--mg-danger)",
					custom: "var(--mg-primary)",
					holiday: "var(--mg-success)",
				};
				return <Tag style={{ backgroundColor: colors[type as keyof typeof colors], color: "white" }}> {type.toUpperCase()} </Tag>;
			},
		},
		{
			title: "Start Date",
			dataIndex: "start_date",
			key: "start_date",
			render: (date: string) => dayjs(date).format("MMM D, YYYY"),
		},
		{
			title: "End Date",
			dataIndex: "end_date",
			key: "end_date",
			render: (date: string) => dayjs(date).format("MMM D, YYYY"),
		},
		{
			title: "Reason",
			dataIndex: "reason",
			key: "reason",
			render: (text: string) => text || "-",
		},
		{
			title: "Status",
			key: "status",
			render: (_: any, record: ScheduleOverride) => {
				const now = dayjs();
				const start = dayjs(record.start_date);
				const end = dayjs(record.end_date);

				if (now.isBefore(start)) {
					return <Tag style={{ backgroundColor: "var(--mg-bg-subtle)", color: "var(--mg-text)" }}> Upcoming </Tag>;
				} else if (now.isAfter(end)) {
					return <Tag style={{ backgroundColor: "var(--mg-bg-subtle)", color: "var(--mg-text)" }}> Past </Tag>;
				} else {
					return <Tag style={{ backgroundColor: "var(--mg-warning)", color: "white" }}> Active </Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: ScheduleOverride) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={!isEditing}>
						Edit
					</Button>
					<Popconfirm title="Are you sure you want to delete this override?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No" disabled={!isEditing}>
						<Button type="link" size="small" danger icon={<DeleteOutlined />} disabled={!isEditing}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<SectionCard
			title={
				<Space>
					<CalendarOutlined />
					<span>Leave &amp; schedule changes</span>
				</Space>
			}
			extra={
				isEditing && (
					<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAdd}>
						Add Override
					</Button>
				)
			}
			noPadding
		>
			<div style={{ padding: 20 }}>
				{/* A date range is the one thing the calendar's day-at-a-time drag cannot express — D17 keeps both. */}
				<Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
					Use this for a dated range — annual leave, a conference, a changed working week. For one slice of one day, drag it out on the appointments calendar instead.
				</Typography.Paragraph>
				<Table columns={columns} dataSource={overrides} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />
			</div>

			<Modal title={editingOverride ? "Edit Schedule Override" : "Add Schedule Override"} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} width={600} okText={editingOverride ? "Update" : "Create"}>
				<Form form={form} layout="vertical">
					<Form.Item name="date_range" label="Date Range" rules={[{ required: true, message: "Please select date range" }]}>
						<RangePicker style={{ width: "100%" }} format="MMM D, YYYY" />
					</Form.Item>

					<Form.Item name="override_type" label="Override Type" rules={[{ required: true, message: "Please select override type" }]}>
						<Select>
							<Select.Option value="block"> Block(Unavailable) </Select.Option>
							<Select.Option value="custom"> Custom Schedule </Select.Option>
							<Select.Option value="holiday"> Holiday </Select.Option>
						</Select>
					</Form.Item>

					<Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.override_type !== currentValues.override_type}>
						{({ getFieldValue }) =>
							getFieldValue("override_type") === "custom" ? (
								<Form.Item label="Custom Availability">
									<ScheduleBlockEditor blocks={scheduleBlocks} onChange={setScheduleBlocks} />
								</Form.Item>
							) : null
						}
					</Form.Item>

					<Form.Item name="reason" label="Reason (Optional)">
						<TextArea rows={3} placeholder="e.g., Annual leave, Conference attendance" />
					</Form.Item>
				</Form>
			</Modal>
		</SectionCard>
	);
};

export default UserProfileScheduleOverrides;
